import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Button,
  alpha,
  IconButton,
  Switch,
} from '@mui/material';
import { ThemeStyles } from '../../theme';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useCategories } from '../../contexts/CategoriesContext';
import HotelCard from './HotelCard';
import HotelCardSkeleton from './HotelCardSkeleton';
import AdvancedFiltersDialog from '../dialogs/AdvancedFiltersDialog';

// Custom ScrollContainer component using theme engine
const ScrollContainer = React.forwardRef(({ style, ...props }, ref) => (
  <Box
    ref={ref}
    {...props}
    component="ul"
    sx={(theme) => ({
      margin: 0,
      padding: '0px 28px 0px 28px', // No top padding needed since search is in normal flow
      listStyle: 'none',
      // Scrolling is handled by the parent list container to keep search sticky
      ...ThemeStyles.scrollbar(theme),
      ...style
    })}
  />
));

ScrollContainer.displayName = 'ScrollContainer';

const HotelList = React.memo(({
  searchQuery,
  onSearchChange,
  onAdvancedFiltersOpen,
  filters,
  filteredHotels,
  selectedHotel,
  onHotelSelect,
  filterLoading,
  dataReady,
  setFilters,
  setFilteredHotels,
  hotels,
  createFilterOptions,
  searchInputRef,
  filterDialogOpen,
  setFilterDialogOpen,
  onFilterChange,
  sidebarCollapsed,
  onToggleSidebar,
  showArchivedHotels,
  onToggleArchivedHotels
}) => {
  const { categories } = useCategories();
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

  // Extract clear filters handler
  const handleClearFilters = useCallback(() => {
    setFilters({});
    setFilteredHotels(hotels);
  }, [setFilters, setFilteredHotels, hotels]);
  
  const handleOpenFiltersDialog = () => {
    setAdvancedFiltersOpen(true);
  };
  
  const handleCloseFiltersDialog = () => {
    setAdvancedFiltersOpen(false);
  };

  // Memoize hotel list content
  const HotelListContent = useMemo(() => {
    if (!dataReady || filterLoading) {
      return (
        <ScrollContainer>
          {Array.from({ length: 20 }).map((_, index) => (
            <HotelCardSkeleton key={index} />
          ))}
        </ScrollContainer>
      );
    }

    if (filteredHotels.length === 0) {
      return (
        <ScrollContainer>
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              No results found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters
            </Typography>
            {Object.keys(filters).length > 0 && (
              <Button
                onClick={handleClearFilters}
                startIcon={<FilterListIcon />}
                sx={{ mt: 2 }}
              >
                Clear all filters
              </Button>
            )}
          </Box>
        </ScrollContainer>
      );
    }

    return (
      <ScrollContainer>
        {/* Search results count message */}
        {(searchQuery || Object.keys(filters).length > 0) && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 1.5, 
              borderRadius: 2,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
              border: '1px solid',
              borderColor: theme => alpha(theme.palette.primary.main, 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'primary.main',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              <SearchIcon sx={{ fontSize: 18, flexShrink: 0 }} />
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {filteredHotels.length} {filteredHotels.length === 1 ? 'hotel' : 'hotels'} found
                {searchQuery && ` for "${searchQuery}"`}
                {Object.keys(filters).length > 0 && ' with filters'}
              </Box>
            </Typography>
            {Object.keys(filters).length > 0 && (
              <Button
                size="small"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'error.main',
                    bgcolor: theme => alpha(theme.palette.error.main, 0.08)
                  }
                }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        )}
        
        {filteredHotels.map(hotel => (
          <HotelCard
            key={hotel.id}
            hotel={hotel}
            selected={selectedHotel?.id === hotel.id}
            onClick={() => onHotelSelect(hotel)}
          />
        ))}
      </ScrollContainer>
    );
  }, [
    dataReady,
    filterLoading,
    filteredHotels,
    selectedHotel?.id,
    onHotelSelect,
    filters,
    handleClearFilters,
    searchQuery
  ]);

  return (
    <>
      <Box sx={{ 
        width: { xs: '100%', md: '400px' },
        height: { xs: (selectedHotel ? '0px' : '100vh'), md: '100vh' },
        maxHeight: { xs: '100vh', md: '100vh' },
        // Remove border for clean minimal look
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        visibility: { xs: (selectedHotel ? 'hidden' : 'visible'), md: 'visible' },
        opacity: { xs: (selectedHotel ? 0 : 1), md: 1 },
        bgcolor: 'background.default', // Switched to default (now lighter)
        // Seamless integration with header
        borderTop: 'none', // Remove top border for seamless connection
        boxShadow: { md: 'none' }, // Remove shadow since it's integrated
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth transitions
        overflowY: 'auto', // Make sidebar the scroll container
      }}>
        {/* Search Area - Fixed at top */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          pt: { xs: '85px', sm: 3.5 }, // Account for fixed header height on mobile
          px: { xs: 3, sm: 4 }, // Increased horizontal padding to match header
          pb: 2.5, // Slightly increased bottom padding
          // Clean background without frosted glass
          bgcolor: 'background.default', // Switched to default (now lighter)
          // Remove all borders and dividers
          // Remove shadows for minimal look
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          flexShrink: 0, // Prevent shrinking
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            width: '100%',
            height: '60px',
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: 'none',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
            },
          }}>
            {/* Search Input - Integrated into unified card */}
            <TextField
              inputRef={searchInputRef}
              size="small"
              placeholder="Search hotels..."
              value={searchQuery}
              onChange={onSearchChange}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  height: '60px',
                  bgcolor: 'transparent',
                  borderRadius: 0,
                  border: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    bgcolor: 'transparent',
                  },
                  '&.Mui-focused': {
                    bgcolor: 'transparent',
                  }
                },
                '& .MuiOutlinedInput-input': {
                  pl: 3,
                  pr: 2,
                  py: 1.75,
                  fontSize: '1rem',
                  fontFamily: 'StrawberrySansText',
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                  '&::placeholder': {
                    color: 'text.secondary',
                    fontWeight: 400,
                    opacity: 0.8
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.75, mr: -0.5 }}>
                    <SearchIcon sx={{ 
                      color: 'text.secondary',
                      fontSize: '1.3rem',
                      transition: 'all 0.3s ease'
                    }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => onSearchChange({ target: { value: '' } })}
                      size="small"
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'text.primary',
                          bgcolor: theme => alpha(theme.palette.error.main, 0.08)
                        },
                        transition: 'all 0.2s ease',
                        mr: 0.75
                      }}
                    >
                      <ClearIcon sx={{ fontSize: '1.1rem' }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Divider */}
            <Box sx={{ 
              width: '1px', 
              height: '30px', 
              bgcolor: 'divider',
              mx: 1
            }} />

            {/* Filter Button - Integrated into unified card */}
            <Button
              variant="text"
              onClick={handleOpenFiltersDialog}
              sx={{
                minWidth: 'auto',
                height: '60px',
                px: 2,
                borderRadius: 0,
                border: 'none',
                color: Object.keys(filters).length > 0 ? 'primary.main' : 'text.secondary',
                bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
                    color: 'primary.main',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontFamily: 'StrawberrySansText',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '1rem',
              }}
            >
              <FilterListIcon sx={{ 
                fontSize: '1.2rem',
                mr: { xs: 0, sm: 0.5 },
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: Object.keys(filters).length > 0
                  ? 'rotate(180deg)'
                  : 'rotate(0deg)'
              }} />
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Filters</Box>
              {Object.keys(filters).length > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    minWidth: '18px',
                    textAlign: 'center'
                  }}
                >
                  {Object.keys(filters).length}
                </Box>
              )}
            </Button>
          </Box>

          {/* Archived Hotels Toggle */}
          <Box sx={{ 
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: '60px',
              bgcolor: 'background.paper',
              borderRadius: 3,
              border: 'none',
              px: 3,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
              },
            }}
            onClick={onToggleArchivedHotels}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ArchiveIcon sx={{ 
                  fontSize: '1.3rem', 
                  color: showArchivedHotels ? 'warning.main' : 'text.secondary',
                  transition: 'color 0.3s ease'
                }} />
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'StrawberrySansText',
                    color: showArchivedHotels ? 'text.primary' : 'text.secondary',
                    fontWeight: 500,
                    fontSize: '1rem',
                    letterSpacing: '0.01em',
                    transition: 'color 0.3s ease'
                  }}
                >
                  Show Archived Hotels
                </Typography>
              </Box>
              <Switch
                checked={showArchivedHotels}
                onChange={onToggleArchivedHotels}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'warning.main',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    bgcolor: 'warning.main',
                    opacity: 0.5,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Hotel List - Scrollable area below search */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth transition for positioning
        }}>
          {HotelListContent}
        </Box>


      </Box>

      {/* Add the AdvancedFiltersDialog */}
      <AdvancedFiltersDialog
        open={advancedFiltersOpen}
        onClose={handleCloseFiltersDialog}
        categories={categories}
        filters={filters}
        onFilterChange={onFilterChange}
        setFilters={setFilters}
        setFilteredHotels={setFilteredHotels}
        hotels={hotels}
        createFilterOptions={createFilterOptions}
      />
    </>
  );
});

HotelList.displayName = 'HotelList';

export default HotelList; 